import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import PropTypes from 'prop-types';
import ANALYTICS from 'constants/analytics';
import actions from '@ehi/global-marketing-interface/lib/actions';
import utils from '@ehi/global-marketing-interface/lib/utils';
import useActions from 'hooks/useActions';
import MODAL, { MODAL_THEMES, MODAL_BUTTON_TYPES } from 'constants/modals';
import { ModalFooter } from 'components/Modal/ModalFooter';
import { i18n } from 'utils/i18n';
import { safeExecute } from 'utils/validation';
import { analyticsInteraction } from 'utils/analytics';
import { capitalizeAll, capitalize } from 'utils/string';
import { skipAnalyticsForLocationTypeFilter } from 'actions/locationFinder';
import { Form } from 'react-final-form';
import Checkbox from 'components/Form/Checkbox';
import THEMES from 'constants/themes';
import { setFiltersByValue } from 'actions/filter/setFiltersByValue';

const { ACCEPT, DECLINE } = MODAL_BUTTON_TYPES;

/**
 * LocationTypeFilterModal
 *
 * @returns {JSX}
 */

const LocationTypeFilterModal = ({ onChange, storeKey, filterConfig, currentFilters }) => {
  const locationFilters =
    utils.isArrayNotEmpty(currentFilters) && currentFilters.length > 1 ? currentFilters.slice(0, 1) : currentFilters;
  const clearFiltersUi = useActions((key) => actions.setFiltersUiByKey(key, []));
  const skipAnalyticsLocationFinder = useActions((bool) => skipAnalyticsForLocationTypeFilter(bool));
  const setFiltersByValueAction = useActions(setFiltersByValue);
  const [locationStateFilters, setLocationStateFilters] = useState(currentFilters);
  const [clearAllState, setClearAllState] = useState(false);

  useEffect(() => {
    setLocationStateFilters(currentFilters);
  }, [locationStateFilters]);

  const handleFilterChange = (params) => {
    setFiltersByValueAction({ storeKey, currentFilters, ...params });
    safeExecute(onChange, currentFilters, params.value);
  };

  // handle modal close and analytics on close modal
  const handleModalClose = (handleClose) => {
    setClearAllState(false);
    handleClose({ skipAnalytics: true });
  };

  const handleCheckboxChange =
    ({ keyPath, isRadio, isSingleSelect, value }) =>
    (e, input, checked, label) => {
      let data = locationFilters;
      const availableItems = locationFilters[0]?.available;
      if (utils.isArrayNotEmpty(availableItems) && utils.isArrayNotEmpty(locationFilters)) {
        const filtered = availableItems.map((item) => {
          const currentValue =
            item.value === value ? { keyPath, isRadio, isSingleSelect, value, checked } : { checked: item.checked };
          return {
            ...item,
            ...currentValue,
          };
        });
        let filteredValues = data[0].values || [];
        filteredValues = filteredValues.filter((val) => val !== value || checked);
        data[0].available = [...filtered];
        if (checked) {
          const values = [...filteredValues, value];
          data[0].values = [...new Set(values)];
        } else {
          data[0].values = [...new Set(filteredValues)];
        }

        data = [...data];
      }
      setLocationStateFilters([...data]);
      clearAllState && setClearAllState(false);
      analyticsInteraction(ANALYTICS.FILTER, ANALYTICS.LOCATION, checked ? `Added ${label}` : `Removed ${label}`);
    };

  const handleApplySearchFilter = (handleClose) => () => {
    skipAnalyticsLocationFinder(true);
    if (clearAllState) {
      clearFiltersUi(storeKey);
    } else {
      const availableItems = locationFilters[0]?.available;
      availableItems.forEach((item) => {
        handleFilterChange({
          keyPath: locationFilters[0].keyPath,
          isRadio: item.isRadio,
          isSingleSelect: item.isSingleSelect,
          checked: item.checked,
        });
      });
    }
    handleModalClose(handleClose);
  };

  const handleClear = () => {
    let data = locationFilters;
    const availableItems = locationFilters[0]?.available;
    if (utils.isArrayNotEmpty(availableItems) && utils.isArrayNotEmpty(locationFilters)) {
      const filtered = availableItems.map((item) => ({
        ...item,
        checked: false,
      }));
      data[0].available = [...filtered];
      data[0].values = [];
      data = [...data];
    }

    setLocationStateFilters([...data]);
    setClearAllState(true);

    analyticsInteraction(
      ANALYTICS.FILTER,
      i18n('location_type_filter_modal_title'),
      `${capitalizeAll(ANALYTICS.CLEAR_ALL)}`
    );
  };

  const onSubmit = () => {};

  const handleFilter = () => {
    const locationAvailabilityFilters = locationFilters;
    return (
      <Form onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <form className='filterby-location-type-modal__form' onSubmit={handleSubmit}>
            {locationAvailabilityFilters.map((filter) => {
              const { available, category, keyPath } = filter;

              const availableSorted =
                category !== 'Vehicle Class' ? utils.sortArrayOfObjectsByKey(available, 'label') : available;

              const config = filterConfig.find((item) => item.keyPath === keyPath);
              if (config) {
                const { isRadio, labelHandler } = filterConfig;
                return (
                  <fieldset key={keyPath} className='filterby-location-type-modal'>
                    <div className='modal-themed__filters-group'>
                      {availableSorted.map(({ checked, label, value }) => {
                        const id = `${keyPath}_${value}`;
                        return (
                          <div key={id} className='modal-themed__filters-group-item'>
                            <Checkbox
                              checked={checked}
                              id={id}
                              key={id}
                              label={labelHandler?.(label, value) || label || value}
                              name={keyPath}
                              onChange={handleCheckboxChange({ keyPath, isRadio, value })}
                              theme={THEMES.LIGHT}
                              derivedState
                            />
                          </div>
                        );
                      })}
                    </div>
                  </fieldset>
                );
              }
              return null;
            })}
          </form>
        )}
      </Form>
    );
  };

  const islocationAvailability = utils.isArrayNotEmpty(locationFilters)
    ? locationFilters.filter((item) => item.keyPath !== 'location_availability')
    : [];
  const NoLocationFilterOptions =
    utils.isArrayNotEmpty(locationFilters) && locationFilters.length > 1 ? true : islocationAvailability?.length > 0;
  return (
    <Modal
      modalKey={MODAL.LOCATION_TYPE_FILTER_MODAL}
      header={i18n('location_type_filter_modal_title')}
      clearFilter={i18n('clear_all_filter_title')}
      theme={MODAL_THEMES.WHITE}
      customClass={'location-type__modal'}
      headerRowWithClearButton={true}
      customActionOnClose={() => {
        setClearAllState(false);
        analyticsInteraction(
          capitalize(ANALYTICS.MODAL),
          i18n('location_type_filter_modal_title'),
          capitalize(ANALYTICS.CLOSE)
        );
      }}
      // the "clear modal queue" action is often called in place of "close modal" - middleware.js
      // We need to pass skip analytics for the "clearModalQueue" action if we are pushing _analytics.interaction manually on open/close modal
      handleModalQueue={{ skipAnalytics: true }}
      handleClearAll={handleClear}
    >
      {({ handleClose }) => (
        <>
          <div className='modal-themed__filters'>
            <div className='modal-themed__horizontal-separator' />
            <p id={`${MODAL.LOCATION_TYPE_FILTER_MODAL}_desc`} className='modal-themed__paragraph'>
              {i18n('location_type_filter_modal_description')}
            </p>
          </div>
          {NoLocationFilterOptions && handleFilter()}
          <div className='column-reverse-footer'>
            <ModalFooter
              buttons={[
                {
                  type: DECLINE,
                  label: i18n('common_cancel'),
                  handler: () => handleModalClose(handleClose),
                  analyticsHandler: () =>
                    analyticsInteraction(
                      capitalizeAll(ANALYTICS.MODAL),
                      i18n('location_type_filter_modal_title'),
                      capitalizeAll(ANALYTICS.CANCEL)
                    ),
                },
                {
                  type: ACCEPT,
                  handler: handleApplySearchFilter(handleClose),
                  label: i18n('apply_search'),
                  analyticsHandler: () =>
                    analyticsInteraction(
                      capitalizeAll(ANALYTICS.MODAL),
                      i18n('location_type_filter_modal_title'),
                      capitalizeAll(ANALYTICS.APPLY_FILTER)
                    ),
                },
              ]}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default LocationTypeFilterModal;

LocationTypeFilterModal.propTypes = {
  onChange: PropTypes.func,
};
